import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import { useTranslation } from '../contexts/TranslationContext';
import { BuildingLibraryIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { Helmet } from 'react-helmet-async';
import { Reveal, PageHero } from './LandingShared';

/**
 * IfaCorporateFinance Component
 * A modern, interactive page describing the corporate finance services for IFAs.
 */
const IfaCorporateFinance = () => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <Helmet>
        <title>IFA Corporate Finance — Cogent Platform</title>
        <meta name="description" content="Explore corporate finance services for Independent Financial Advisors (IFAs). Cogent Asset Management provides tailored solutions for IFA businesses and their clients." />
        <link rel="canonical" href="https://cogentplatform.com/ifa-corporate-finance" />
      </Helmet>
      <Navbar />

      <main className="flex-grow">
        {/* Hero Banner */}
        <PageHero title={t('ifaCorporateFinance.title')} />

        {/* Content Section */}
        <div className="bg-gradient-to-b from-white to-slate-50 py-16 sm:py-20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden">
                {/* Gradient accent line at top */}
                <div className="h-1 bg-gradient-to-r from-blue-600 to-cyan-500" />

                <div className="p-8 sm:p-12 text-center">
                  {/* Icon in gradient container */}
                  <Reveal delay={100}>
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center shadow-lg mx-auto">
                      <BuildingLibraryIcon className="h-8 w-8 text-white" />
                    </div>
                  </Reveal>

                  {/* Heading */}
                  <Reveal delay={200}>
                    <h2 className="mt-6 text-3xl font-bold text-gray-900">
                      {t('ifaCorporateFinance.heading')}
                    </h2>
                  </Reveal>

                  {/* Gradient divider */}
                  <Reveal delay={300}>
                    <div className="mt-4 w-20 h-1 bg-gradient-to-r from-blue-600 to-cyan-500 mx-auto" />
                  </Reveal>

                  {/* Description */}
                  <Reveal delay={400}>
                    <p className="mt-8 max-w-3xl mx-auto text-xl text-gray-600 leading-relaxed">
                      {t('ifaCorporateFinance.description')}
                    </p>
                  </Reveal>

                  {/* CTA Button */}
                  <Reveal delay={500}>
                    <div className="mt-10">
                      <Link
                        to="/contact"
                        className="group inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200"
                      >
                        <span>{t('ifaCorporateFinance.cta')}</span>
                        <ArrowRightIcon className="ml-3 h-5 w-5 transform group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </div>
                  </Reveal>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default IfaCorporateFinance;
